import { Graphics, Container, ContainerChild, Sprite, Texture } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetCountdown,
} from "../../../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../../../Services/TimerService";

import { getTimeParts } from "../Countdown.utils";

import type { ICountdownTheme } from "./ICountdownTheme";

import {
  renderCountdownDesignSvg,
  type CountdownDesign,
  type CountdownParts,
} from "../../../../../../data/countdownDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "../../svgTextureRaster";

/**
 * Design-store render strategy for the Countdown widget — mirrors the Timer
 * widget's design path (`TimerWidget.ts → renderDesign`). When the widget's
 * `options.designId` resolves to a {@link CountdownDesign}, we render that
 * design's CARD SVG (from the local catalog) as a single Pixi sprite stretched
 * to the widget bounds, then keep it live by re-rasterising the texture only
 * when the displayed D:H:M:S string changes (≈1/sec, driven by the same
 * `TimerService` tick the text themes use) — never every animation frame.
 *
 * The design itself (which CountdownDesign) is resolved by the parent
 * `CountdownWidgetRenderer`; this strategy is constructed with it so the
 * strategy-swap logic only re-instantiates when the design actually changes.
 *
 * If `designId` is absent/unknown the parent never builds this strategy and
 * the legacy text/Theme1 render is used unchanged.
 */
export class DesignRenderer implements ICountdownTheme {
  private timerCallback: ((time: number) => void) | null = null;

  private container: Container | null = null;

  private sprite: Sprite | null = null;

  /** Widget bounds the sprite is stretched to — re-asserted after each swap. */
  private destWidth = 1;

  private destHeight = 1;

  /** Backing-store multiplier for the rasterised card bitmap. */
  private renderScale = 1;

  /** Last D:H:M:S string baked into the current texture. */
  private lastTime: string | null = null;

  /** Texture currently shown — destroyed before being replaced. */
  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  constructor(private readonly design: CountdownDesign) {}

  render(
    widget: IStudioWidget,

    parentContainer: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetCountdown).options;

    this.container = new Container();

    parentContainer.addChild(this.container);

    // Optional solid backdrop, same semantics as the text themes.
    const backgroundColor = options.backgroundColor;

    if (backgroundColor && backgroundColor !== "transparent") {
      const bg = new Graphics();

      bg.rect(0, 0, destination.width, destination.height).fill(
        backgroundColor,
      );

      this.container.addChild(bg);
    }

    const targetDate = options.targetDate
      ? new Date(options.targetDate)
      : new Date();

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    // Backing-store resolution: rasterise at the larger of the card's native
    // size and the on-canvas size, capped so an oversized widget can't ask for
    // a multi-thousand-px bitmap. The sprite is then scaled to the bounds.
    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Draw the first frame immediately with whatever fonts are already decoded
    // so the slot is never empty; the post-font draw upgrades the typeface once
    // the design's Google fonts are ready.
    void this.rerasterize(this.computeTime(targetDate, options));
    void ensureDesignFontsReady(this.design.fonts).then(() => {
      if (this.destroyed) return;
      // Redraw with the CURRENT time now that the real fonts are decoded (the
      // first draw may have used a system fallback, and the clock may have
      // advanced while the face was loading). Force it past the equality guard.
      this.lastTime = null;
      void this.rerasterize(this.computeTime(targetDate, options));
    });

    const timerService = TimerService.getInstance();

    const update = (_now: number) => {
      if (this.destroyed) return;
      const timeString = this.computeTime(targetDate, options);
      // Re-raster ONLY when the D:H:M:S string actually changed — not every
      // tick and never every frame.
      if (timeString !== this.lastTime) {
        void this.rerasterize(timeString);
      }
    };

    update(Date.now());

    this.timerCallback = update;

    timerService.subscribe(update);
  }

  /**
   * Compute the four zero-padded D/H/M/S strings for the current instant and
   * join them into a single comparison key (`DD:HH:MM:SS`). The card always
   * shows all four units regardless of the legacy `showDays`/`showHours`…
   * toggles (the design's layout is fixed at four blocks), so we pass an empty
   * options object to `getTimeParts` to get the full, un-collapsed breakdown.
   */
  private computeParts(
    targetDate: Date,
    _options: IStudioWidgetCountdown["options"],
  ): CountdownParts {
    const { days, hours, minutes, seconds } = getTimeParts(targetDate, {});
    const pad = (num: number) => String(num).padStart(2, "0");
    return {
      dd: pad(days),
      hh: pad(hours),
      mm: pad(minutes),
      ss: pad(seconds),
    };
  }

  private computeTime(
    targetDate: Date,
    options: IStudioWidgetCountdown["options"],
  ): string {
    const p = this.computeParts(targetDate, options);
    return `${p.dd}:${p.hh}:${p.mm}:${p.ss}`;
  }

  /**
   * (Re)build the card SVG → texture for the given `DD:HH:MM:SS` string and
   * swap it onto the sprite. Generation-guarded so a slow decode that resolves
   * after a newer one can't clobber the fresher frame, and after teardown.
   */
  private async rerasterize(timeString: string): Promise<void> {
    if (this.destroyed || !this.sprite) return;

    // Claim this generation/string immediately so the equality guard in the
    // tick callback doesn't fire a second raster for the same string
    // mid-decode.
    this.lastTime = timeString;
    const gen = ++this.generation;

    const [dd, hh, mm, ss] = timeString.split(":");
    const svg = renderCountdownDesignSvg(this.design, { dd, hh, mm, ss });

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      // A newer raster (or teardown) won the race — drop this one.
      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      // Assigning a texture in Pixi v8 resets the sprite to the texture's
      // intrinsic (backing-store) size — re-assert the widget bounds so the
      // card always fills `destination` regardless of renderScale.
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      // Decode failed (malformed SVG / browser quirk) — keep the previous frame
      // on screen rather than blanking. Allow a future tick to retry by
      // clearing the cached string.
      if (!this.destroyed && gen === this.generation) {
        this.lastTime = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.timerCallback) {
      TimerService.getInstance().unsubscribe(this.timerCallback);

      this.timerCallback = null;
    }

    // Release the off-screen texture we own. The sprite is destroyed with its
    // container below.
    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;

    if (this.container) {
      this.container.destroy({ children: true });

      this.container = null;
    }
  }
}
